// Metal prices (our public API, which reads our DB — never the provider).
// Fetched without the login token: the same public data for visitors and
// members. A result is reused for 2 minutes, but an empty list is never
// kept, so the ticker appears as soon as the first prices are stored.
export async function publicGet(path) {
  const res = await fetch(`/api${path}`, { cache: 'no-cache', headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`prices_${res.status}`);
  return res.json();
}

let cache = null;
export function loadPrices({ fresh = false } = {}) {
  if (fresh || !cache || Date.now() - cache.at > 2 * 60 * 1000) {
    const promise = publicGet('/prices').then((d) => {
      if (!d?.metals?.length) cache = null;
      return d;
    });
    cache = { at: Date.now(), promise };
    promise.catch(() => { cache = null; });
  }
  return cache.promise;
}
export const loadPriceHistory = () => publicGet('/prices/history');

const locale = (lang) => (lang === 'en' ? 'en-CA' : 'fr-CA');
export const metalName = (m, lang) => (lang === 'en' ? m.nameEn : m.nameFr);

// Base metals per tonne without cents; precious metals per troy ounce.
export function formatMetalPrice(m, lang) {
  const digits = m.kind === 'base' ? 0 : 2;
  return new Intl.NumberFormat(locale(lang), { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(m.price);
}
export const unitLabel = (m, t) => t(m.unit === 't' ? 'prices.perTonne' : 'prices.perOunce');

export function formatChange(m, lang) {
  if (m.changePct === null || m.changePct === undefined) return null;
  const pct = new Intl.NumberFormat(locale(lang), { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always' }).format(m.changePct);
  const dir = m.change > 0 ? 'up' : m.change < 0 ? 'down' : 'flat';
  return { dir, arrow: dir === 'up' ? '▲' : dir === 'down' ? '▼' : '■', text: `${pct} %`.replace(' %', lang === 'en' ? '%' : ' %') };
}

// e.g. "10 oct. 2026, 14 h 00 HAE" (Montreal time). dateStyle/timeStyle
// can't be combined with timeZoneName (throws), so the fields are explicit.
export function formatUpdated(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale(lang), {
      timeZone: 'America/Montreal', year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(d);
  } catch {
    return d.toLocaleString(locale(lang));
  }
}

// Exchange rates: CAD for 1 unit (USD 1.4271; JPY 0.00902).
export function formatRate(rate, lang) {
  const opts = rate >= 1 ? { minimumFractionDigits: 4, maximumFractionDigits: 4 } : { maximumSignificantDigits: 4 };
  return new Intl.NumberFormat(locale(lang), opts).format(rate);
}
export function formatDay(day, lang) {
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  return new Intl.DateTimeFormat(locale(lang), { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}
